import { useEffect, useState, useRef } from "react";
import Moves from "./3Moves";
import {
  destructureLine,
  getLines,
  getMove,
  getPrevMoveNotation,
  isMoveValid,
  parseMove,
} from "../../util/Chess";
import { getCurrentBoardPosition } from "./UseGameState";
import constants from "../../util/constants";
import { filterInPlace } from "../../util/helpers";
import clsx from "clsx";
import { WHITE } from "chess.js";
import { isProfane, cleanProfanity } from "../../util/filterProfanity";
import { checkRateLimit } from "../../util/rateLimiter";
import { getRateLimit, buildRateKey } from "../../util/rateLimitConfig";

const MOVE_TYPES = constants.CHESSBOARD.MOVE_TYPES;

const AddComment = ({
  moves,
  currentMoveIdx,
  setCurrentMoveIdx,
  setMoves,
  handleAddComment,
  updateContentForMove,
  variationToComment = null,
  onVariationCommented = () => { },
  handleDeleteCurrentCommentLine = () => { },
  handleUndoCommentMove = () => { },
  isVariationActive = false,
  isUserAuthenticated = () => true,
  setShowLoginPrompt = () => {},
  showDeleteConfirmation = () => { },
  onInputFocusChange = () => {},
  userId,
  gameId,
  showRateLimitError
}) => {
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  const renderCount = useRef(0);
  renderCount.current++;

  

  const [comment, setComment] = useState("");
  const [moveContent, setMoveContent] = useState("");
  const [error, setError] = useState(false);
  const [rateLimitError, setRateLimitError] = useState("");
  const [isVariationComment, setIsVariationComment] = useState(false);
  const [preservedCaptionText, setPreservedCaptionText] = useState("");
  const [isInStableSession, setIsInStableSession] = useState(false);
  
  // NEW: State for move-linking functionality
  const [linkToMove, setLinkToMove] = useState(false);
  
  // FIX: Use useRef to preserve the variation title across moves, instead of useState that resets
  const variationTitleRef = useRef("");
  // Add a state to trigger re-renders for the input field
  const [variationTitleDisplay, setVariationTitleDisplay] = useState("");
  
  // Track if we're currently in a variation session to avoid resetting on move changes
  const isCommentingSessionActiveRef = useRef(false);
  
  // Track component mounting state to prevent memory leaks
  const isMountedRef = useRef(true);
  
  // Add content management state
  const [isContentFocused, setIsContentFocused] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState("");
  const previousMoveIdxRef = useRef(currentMoveIdx);
  
  // NEW: Track the parent move to return to after deletion
  const parentMoveToReturnTo = useRef(null);
  
  // NEW: Track if navigation is blocked by pending deletion
  const [navigationBlocked, setNavigationBlocked] = useState(false);

  // NEW: Check if we're viewing an already-submitted comment after all hooks are declared
  // If so, don't show the AddComment interface at all
  const currentMove = getMove(moves, currentMoveIdx);
  const isViewingSubmittedComment = currentMove && currentMove.creator && currentMove.creator.id !== -1;
  
  

  // Helper function to get the correct parent move to return to
  const getParentMoveToReturnTo = (currentMoveIdx) => {
    if (currentMoveIdx.includes('|')) {
      // We're in a variation - get the parent move where the variation branched from
      const parts = currentMoveIdx.split('|');
      
      if (parts.length === 2) {
        // First-level variation (e.g., "5|0:1") - parent is the main line move (move 5)
        const parentMoveIdx = parseInt(parts[0]);
        return String(parentMoveIdx);
      } else {
        // Nested variation (e.g., "5|0:1|1:2") - parent is the move where this nested variation branched
        // Remove the last segment to get the parent path
        const parentPath = parts.slice(0, -1).join('|');
        return parentPath;
      }
    } else {
      // We're in main line - if we're creating a variation from a main line move,
      // we should return to that same main line move
      const currentMoveInt = parseInt(currentMoveIdx);
      return String(currentMoveInt);
    }
  };

  // Track component lifecycle
  useEffect(() => {
    
    return () => {
      
    };
  }, []);

  // Track all prop changes
  useEffect(() => {
    
  }, [variationToComment, currentMoveIdx, isVariationActive]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Ensure all comment session state is cleaned up on unmount
      isCommentingSessionActiveRef.current = false;
      setNavigationBlocked(false);
      parentMoveToReturnTo.current = null;
    };
  }, []);

  // HIGH-LEVEL DEBUGGING: Add global keyboard event listener
  useEffect(() => {
    const globalKeyHandler = (e) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        
        
        // Specifically log RIGHT arrow events
        if (e.key === 'ArrowRight') {
         
          
          // Test if we can manually call a navigation function
          
          
          // Check if we're focused on an input and at the end of text
          const isInputFocused = ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName);
          const isAtEndOfInput = document.activeElement.selectionStart === document.activeElement.value?.length;
          
          if (!isInputFocused || isAtEndOfInput) {
            
          } else {
            
          }
        }
      }
    };

    // Add listener in capture phase to catch events early
    document.addEventListener('keydown', globalKeyHandler, true);

    return () => {
      document.removeEventListener('keydown', globalKeyHandler, true);
    };
  }, [currentMoveIdx, isContentFocused, isVariationComment]);

  // DIRECT KEYBOARD NAVIGATION: Add keyboard handler specifically for comment moves
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if no text input is focused
      const isInputFocused = ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName);
      
      // Block all navigation if deletion is pending
      if (navigationBlocked) {
        
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      if (!isInputFocused && ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
        
        
        // Get the filtered moves for the current context
        // We need to replicate the logic from the 3Moves component
        // to find comment moves and navigate through them properly
        
        if (e.key === 'ArrowRight') {
          // Navigate to next comment move - NEVER show delete popup for forward navigation
          
          if (currentMoveIdx.includes('|')) {
            // We're in a variation - check if next move in variation exists
            const parts = currentMoveIdx.split('|');
            const parentMoveIdx = parseInt(parts[0]);
            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
            
            // Check if the next move exists in the current variation
            if (moves && moves[parentMoveIdx] && moves[parentMoveIdx].variations && 
                moves[parentMoveIdx].variations[varIdx] && 
                moves[parentMoveIdx].variations[varIdx][moveIdx + 1]) {
              
              // Next move exists, navigate forward
              const nextMoveIdx = moveIdx + 1;
              const nextMoveId = `${parentMoveIdx}|${varIdx}:${nextMoveIdx}`;
              
              setCurrentMoveIdx(nextMoveId);
            } else {
              // At the end of variation, do nothing
              
            }
          } else {
            // We're in main line - check if next move exists
            const currentMoveInt = parseInt(currentMoveIdx);
            if (!isNaN(currentMoveInt) && moves && moves[currentMoveInt + 1]) {
              const nextMoveIdx = currentMoveInt + 1;
              
              setCurrentMoveIdx(nextMoveIdx);
            } else {
              // At the end of main line, do nothing
              
            }
          }
        } else if (e.key === 'ArrowLeft') {
          // Navigate to previous comment move - CHECK POSITION FIRST
          
          if (currentMoveIdx.includes('|')) {
            // We're in a variation - check position FIRST
            const parts = currentMoveIdx.split('|');
            const parentMoveIdx = parts[0];
            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
            
            if (moveIdx > 0) {
              // Middle of variation - just navigate to previous move (no confirmation needed)
              const prevMoveIdx = moveIdx - 1;
              const prevMoveId = `${parentMoveIdx}|${varIdx}:${prevMoveIdx}`;
              
              setCurrentMoveIdx(prevMoveId);
            } else {
              // At beginning of variation - would exit to parent
              // NOW check if we need confirmation for unsaved work
              const currentMove = getMove(moves, currentMoveIdx);
              const isTemporaryCommentMove = currentMove?.creator?.id === -1;
              const hasUnsavedContent = moveContent && moveContent.trim() !== "" && moveContent !== lastSavedContent;
              const hasUnsavedVariationTitle = isVariationComment && variationTitleRef.current && variationTitleRef.current.trim() !== "";
              
              if (isTemporaryCommentMove && (hasUnsavedContent || hasUnsavedVariationTitle || isVariationComment)) {
                
                
                // Store the correct parent move to return to after deletion
                parentMoveToReturnTo.current = parentMoveIdx;
                
                // Block navigation and show delete confirmation
                setNavigationBlocked(true);
                handleEnhancedDeleteConfirmation();
                return; // Don't navigate, let user confirm first
              } else {
                // Safe to exit to parent move
                setCurrentMoveIdx(parentMoveIdx);
              }
            }
          } else {
            // We're in main line - try simple decrement
            const currentMoveInt = parseInt(currentMoveIdx);
            if (!isNaN(currentMoveInt) && currentMoveInt > 0) {
              const prevMoveIdx = currentMoveInt - 1;
              
              setCurrentMoveIdx(prevMoveIdx);
            }
          }
        }
        
        // Prevent default browser behavior
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentMoveIdx, isVariationComment, setCurrentMoveIdx]);

  // Initialize when a variation is passed for commenting
  useEffect(() => {
    if (variationToComment && variationToComment.length > 0) {
      if (!isInStableSession) {
        // First time entering - preserve any existing text
        if (variationTitleRef.current) {
          setPreservedCaptionText(variationTitleRef.current);
        }
        setIsVariationComment(true);
        setIsInStableSession(true);
        setVariationTitleDisplay(preservedCaptionText || variationTitleRef.current);
      } else {
        // Already in session - restore preserved text
        setVariationTitleDisplay(preservedCaptionText);
        variationTitleRef.current = preservedCaptionText;
      }
    } else if (isInStableSession) {
      // Only reset when explicitly exiting
      setIsVariationComment(false);
      setIsInStableSession(false);
      setPreservedCaptionText("");
      variationTitleRef.current = "";
      setVariationTitleDisplay("");
    }
  }, [variationToComment, preservedCaptionText, isInStableSession]);

  // Enhanced content management with auto-save and move change handling
  useEffect(() => {
    const move = getMove(moves, currentMoveIdx);
    
    // Auto-save content for previous move if it changed and we're switching moves
    if (previousMoveIdxRef.current !== currentMoveIdx && 
        previousMoveIdxRef.current !== null && 
        moveContent !== lastSavedContent &&
        moveContent.trim() !== "") {
      
      const previousMove = getMove(moves, previousMoveIdxRef.current);
      if (previousMove?.creator?.id === -1) {
        
        updateContentForMove(moveContent, previousMoveIdxRef.current);
        setLastSavedContent(moveContent);
      }
    }
    
    // Load content for current move (clear if not a comment move or different move)
    if (!move?.creator || move.creator.id !== -1) {
      // Not a comment move, clear content
      setMoveContent("");
      setLastSavedContent("");
    } else {
      // Comment move, load its content
      const currentContent = move.content || "";
      setMoveContent(currentContent);
      setLastSavedContent(currentContent);
    }
    
    // Update previous move reference
    previousMoveIdxRef.current = currentMoveIdx;
  }, [currentMoveIdx, moves, updateContentForMove]);

  // Auto-save on content change (with debouncing) - only for current move
  useEffect(() => {
    if (!moveContent || moveContent === lastSavedContent) return;
    
    const move = getMove(moves, currentMoveIdx);
    if (!move?.creator || move.creator.id !== -1) return;
    
    const timeoutId = setTimeout(() => {
      
      updateContentForMove(moveContent, currentMoveIdx);
      setLastSavedContent(moveContent);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [moveContent, currentMoveIdx, moves, lastSavedContent, updateContentForMove]);

  const handleContentChange = (e) => {
    setMoveContent(cleanProfanity(e.target.value));
  };

  const handleContentFocus = () => {
    setIsContentFocused(true);
    onInputFocusChange(true); // Notify parent that input is focused
  };

  const handleContentBlur = () => {
    setIsContentFocused(false);
    onInputFocusChange(false); // Notify parent that input is blurred
    
    // Save content on blur if it has changed
    if (moveContent !== lastSavedContent) {
      const move = getMove(moves, currentMoveIdx);
      if (move?.creator?.id === -1) {
        
        updateContentForMove(moveContent, currentMoveIdx);
        setLastSavedContent(moveContent);
      }
    }
  };

  // NEW: Enhanced delete handler that returns to the correct parent
  const handleEnhancedDelete = () => {
    
    
    // Call the original delete function
    handleDeleteCurrentCommentLine();
    
    // If we have a stored parent move, navigate to it
    if (parentMoveToReturnTo.current !== null) {
      
      setCurrentMoveIdx(parentMoveToReturnTo.current);
      
      // Clear the stored parent
      parentMoveToReturnTo.current = null;
    }
    
    // Clear navigation block
    setNavigationBlocked(false);
  };

  // NEW: Enhanced delete confirmation that handles proper navigation
  const handleEnhancedDeleteConfirmation = () => {
    
    
    // Create a custom confirmation callback that uses our enhanced delete
    const customConfirmationCallback = () => {
      handleEnhancedDelete();
    };
    
    // Call showDeleteConfirmation but we need to ensure it uses our callback
    // For now, let's just call showDeleteConfirmation and hope the parent handles it correctly
    showDeleteConfirmation();
  };

  // NEW: Handle cancellation of delete (clear navigation block)
  const handleDeleteCancel = () => {
    
    setNavigationBlocked(false);
    parentMoveToReturnTo.current = null;
  };

  // NEW: Function to capture current move data for move-linked comments
  const captureMoveData = () => {
    if (!linkToMove) return null;

    try {
      // Get current move notation and details
      const currentMoveObj = getMove(moves, currentMoveIdx);
      const moveNotation = currentMoveObj?.notation || '';
      
      // Get current FEN - use window.currentFEN if available, otherwise calculate
      const moveFEN = window.currentFEN || getCurrentBoardPosition(moves, currentMoveIdx);
      
      // Get move squares for highlighting (from the current move object)
      const moveFromSquare = currentMoveObj?.from || '';
      const moveToSquare = currentMoveObj?.to || '';
      
      // Get parent move index for variations
      const parentMoveIndex = getParentMoveToReturnTo(currentMoveIdx);
      
      return {
        moveIndex: currentMoveIdx,
        moveNotation: moveNotation,
        moveFEN: moveFEN,
        moveFromSquare: moveFromSquare,
        moveToSquare: moveToSquare,
        parentMoveIndex: parentMoveIndex
      };
    } catch (error) {
      console.error('[AddComment] Error capturing move data:', error);
      return null;
    }
  };

  const submitComment = async () => {
    try {
      setError(false);
      setRateLimitError("");

      // Check authentication
      if (!isUserAuthenticated()) {
        setShowLoginPrompt(true);
        return;
      }

      // Rate limiting check - applies to both variation and regular comments
      const userTier = 'free'; // TODO: Get actual user tier from props/context
      const config = getRateLimit('COMMENT', userTier);
      const rateKey = buildRateKey(userId, gameId, 'comment');
      
      const isLimited = checkRateLimit({
        key: rateKey,
        limit: config.limit,
        windowMs: config.windowMs
      });

      if (isLimited) {
        if (showRateLimitError) {
          showRateLimitError();
        } else {
          setRateLimitError("Please wait a moment and try again.");
        }
        return;
      }

      // Variation comment logic
      if (isVariationComment) {
        const titleToSubmit = variationTitleRef.current.trim();

        if (!titleToSubmit) {
          setError(true);
          return;
        }

        // Clean the title but don't block submission
        const cleanedTitle = cleanProfanity(titleToSubmit);
        
        await handleAddComment(cleanedTitle);
        
        // Reset state after successful submission
        setComment("");
        setMoveContent("");
        variationTitleRef.current = "";
        setVariationTitleDisplay("");
        setIsVariationComment(false);
        setPreservedCaptionText("");
        setLinkToMove(false); // Reset move linking
        
        // Additional state resets to prevent re-rendering issues
        isCommentingSessionActiveRef.current = false;
        setNavigationBlocked(false);
        setIsInStableSession(false);
        parentMoveToReturnTo.current = null;
        
        // Call the callback to notify parent component
        onVariationCommented();
        return;
      }

      // Regular comment logic
      const commentToSubmit = comment.trim();
      if (!commentToSubmit) {
        setError(true);
        return;
      }

      // Clean the comment but don't block submission
      const cleanedComment = cleanProfanity(commentToSubmit);

      // NEW: Capture move data if linking is enabled
      const moveData = captureMoveData();
      
      // Submit comment with optional move data
      await handleAddComment(cleanedComment, moveData);
      
      // Reset all form state
      setComment("");
      setMoveContent("");
      setError(false);
      setLinkToMove(false); // Reset move linking checkbox
      
      // Additional state resets for regular comments
      isCommentingSessionActiveRef.current = false;
      setNavigationBlocked(false);
      parentMoveToReturnTo.current = null;
      
    } catch (error) {
      console.error('[AddComment] Error submitting comment:', error);
      setError(true);
    }
  };

  // If we're viewing an already-submitted comment, don't render the AddComment interface
  if (isViewingSubmittedComment) {
    
    return null; // Don't render anything
  }

  // NEW: Prevent rendering for starting position variations
  // This allows exploration in ViewGame but prevents submission
  if (currentMoveIdx.startsWith('-1|')) {
    // console.log('[AddComment2] Preventing render for starting position variation:', currentMoveIdx);
    return null; // Don't render anything for starting variations
  }

  return (
    <div className="" style={{ width: '', display: 'flex', flexDirection: 'column', alignItems: 'start', marginTop: '', marginBottom: '5px', gap: '3px' }}>
      {/* Show moves from user if commenting on a variation */}
      {isVariationComment ? (
        <div style={{
          borderRadius: '5px',
          minHeight: '165px',
          padding: '10px',
         
          width: '500px'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '10px',
            color: 'gold',
            textAlign: 'center',
            marginTop: '5px'
          }}>
            Your Move Variation:
          </div>
          <Moves
            moves={variationToComment || moves}
            currentMove={currentMoveIdx}
            setCurrentMove={(newMoveIdx) => {
              
              setCurrentMoveIdx(newMoveIdx);
            }}
            starredMoves={[]}
            type={"COMMENTS"}
            userId={-1}
            key={`variation-moves-${JSON.stringify(variationToComment || moves).length}`}
            variationToComment={variationToComment}
            temporaryControls={{
              onDelete: handleEnhancedDeleteConfirmation,
              onUndo: handleUndoCommentMove
            }}
          />
        </div>
      ) : (
        <div>
          <Moves
            moves={moves}
            currentMove={currentMoveIdx}
            setCurrentMove={(newMoveIdx) => {
              
              setCurrentMoveIdx(newMoveIdx);
            }}
            starredMoves={[]}
            type={"COMMENT_FULL"}
            userId={-1}
            key={`regular-moves-${JSON.stringify(moves).length}`}
          />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '7px' }}>
        {/* Move Content Textarea - Only show for comment moves */}
        {(() => {
          const move = getMove(moves, currentMoveIdx);
          return move?.creator?.id === -1 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              marginBottom: '0px',
              padding: '10px',
              marginLeft: '5px'
            }}>
              <label style={{
                color: '#58b047',
                fontSize: '14px',
                marginBottom: '5px',
                fontWeight: 'bold'
              }}>
                Move Annotation:
              </label>
              <textarea
                value={moveContent}
                onChange={handleContentChange}
                onFocus={handleContentFocus}
                onBlur={handleContentBlur}
                placeholder="Add annotations here"
                maxLength="1000"
                style={{
                  height: '60px',
                  padding: '10px',
                  fontSize: '14px',
                  borderRadius: '5px',
                  background: '#222',
                  color: 'white',
                  border: '2px solid #58b047',
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: '60px',
                  maxHeight: '120px',
                  transition: 'border-color 0.2s ease',
                  width: '90%'
                }}
              />
              {/* {moveContent !== lastSavedContent && (
                <div style={{
                  fontSize: '12px',
                  color: '#ff9900',
                  marginTop: '2px',
                  fontStyle: 'italic'
                }}>
                  Auto-saving...
                </div>
              )} */}
            </div>
          ) : null;
        })()}

        {/* Rate limit error message */}
        {rateLimitError && (
          <div style={{
            color: '#ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            border: '1px solid #ff6b6b',
            borderRadius: '5px',
            padding: '10px',
            margin: '5px 0',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            {rateLimitError}
          </div>
        )}

        {/* Title field for variation comments - restructured into a single row */}
        {isVariationComment && (
          <div style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center', 
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px'
          }}>
            <input
              type="text"
              className={clsx("", error && "input-error")}
              value={variationTitleDisplay}
              onChange={(e) => {
                setError(false);
                setRateLimitError("");
                const newValue = cleanProfanity(e.target.value);
                // Update both ref and display state
                variationTitleRef.current = newValue;
                setVariationTitleDisplay(newValue);
                setPreservedCaptionText(newValue);
              }}
              onFocus={() => onInputFocusChange(true)}
              onBlur={() => onInputFocusChange(false)}
              maxLength="1000"
              style={{
                width: "360px",
                height: "40px",
                paddingLeft: '15px',
                fontSize: '18px',
                borderRadius: '5px',
                background: '#222',
                color: 'white',
                border: error ? '3px solid red' : '3px solid #58b047',
                outline: error ? 'none' : '',
              }}
              placeholder={"Caption your variation..."}
            />

            {/* <button
              style={{ 
                height: '48px', 
                backgroundColor: '#333', 
                color: 'white', 
                borderRadius: '5px', 
                border: '2px solid #666', 
                fontSize: '16px',
                padding: '0 15px',
                marginRight: '10px',
                cursor: 'pointer'
              }}
              onClick={() => {
                setIsVariationComment(false);
                setVariationTitle("");
                onVariationCommented(); // Notify parent to clean up
              }}
            > 
              CANCEL
            </button> */}

            <button
              className=""
              style={{
                height: '48px',
                minWidth: '100px',
                backgroundColor: '#58b047',
                color: 'black',
                borderRadius: '5px',
                border: '2px solid #58b047',
                fontSize: '20px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
              onClick={submitComment}
            >
              SUBMIT
            </button>
          </div>
        )}

        {/* Regular comment field if not a variation comment */}
        {!isVariationComment && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '10px'
          }}>
            {/* Move-linking checkbox - only show for regular comments, not variations */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              paddingLeft: '5px'
            }}>
              <input
                type="checkbox"
                id="linkToMove"
                checked={linkToMove}
                onChange={(e) => setLinkToMove(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              <label 
                htmlFor="linkToMove" 
                style={{
                  color: '#ccc',
                  fontSize: '14px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Link comment to current move
                {linkToMove && currentMoveIdx !== '-1' && (
                  <span style={{ color: '#58b047', marginLeft: '8px' }}>
                    ({(() => {
                      const currentMoveObj = getMove(moves, currentMoveIdx);
                      const moveNumber = Math.floor(parseInt(currentMoveIdx.split('|')[0]) / 2) + 1;
                      const isWhite = parseInt(currentMoveIdx.split('|')[0]) % 2 === 0;
                      return `${moveNumber}${isWhite ? '.' : '...'} ${currentMoveObj?.notation || ''}`;
                    })()})
                  </span>
                )}
              </label>
            </div>

            {/* Comment input and send button */}
            <div style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              gap: '10px'
            }}>
              <input
                type="text"
                className={clsx("", error && "input-error")}
                value={comment}
                onChange={(e) => {
                  setError(false);
                  setRateLimitError("");
                  setComment(cleanProfanity(e.target.value));
                }}
                onFocus={() => onInputFocusChange(true)}
                onBlur={() => onInputFocusChange(false)}
                maxLength="1000"
                style={{
                  flex: '1',
                  height: "40px",
                  paddingLeft: '20px',
                  fontSize: '18px',
                  borderRadius: '5px',
                  background: '',
                  color: 'black',
                  border: error ? '3px solid red' : '3px solid #58b047',
                  outline: error ? 'none' : ''
                }}
                placeholder={error ? "YOU MUST TYPE SOMETHING" : "Start typing a comment..."}
              />

              <button
                className=""
                style={{
                  height: '48px',
                  minWidth: '95px',
                  backgroundColor: 'black',
                  color: '#58b047',
                  borderRadius: '5px',
                  border: '2px solid #58b047',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  marginLeft: '5px',
                  cursor: 'pointer'
                }}
                onClick={submitComment}
              >
                SEND
              </button>
            </div>
          </div>
        )}

        {/* Removed the separate buttons container that was previously here */}
      </div>
    </div>
  );
};

export default AddComment;
